import styles from "../../showroomForm.css";
import { PreviewFile } from "./FilePreview.jsx";
import { SelectFile } from "./SelectFile.jsx";

/**
 * A functional component for managing file uploads in a form. This component handles user interactions for selecting and previewing files.
 * It also manages validation and error display for the selected file. Files can be of various accepted types specified through props.
 *
 * @property {string} fieldKey - Identifies the specific key in the form data to associate with this file upload field.
 * @property {string} label - The label displayed for the file upload field in the UI.
 * @property {string} description - The description displayed for the file upload field in the UI.
 * @property {Object} formData - An object representing the current form's data. Used to read and manipulate form field values.
 * @property {Function} setFormData - The function to update the form's data state when a file is selected or removed.
 * @property {string} [accept] - A string specifying the file types that are allowed for upload, formatted as a file input attribute (e.g., ".png,.jpg").
 * @property {boolean} [required] - Indicates if the file upload field is mandatory.
 * @property {Function} [validateFile] - A function to validate the selected file. Typically used to check size, type, etc.
 * @property {string} [error] - A string specifying any error message to be displayed for invalid inputs or upload failures.
 */
export const FileUpload = ({
  fieldKey,
  label,
  description,
  formData,
  setFormData,
  accept,
  required,
  validateFile,
  error,
}) => {
  /**
   * Determines whether a preview should be displayed based on the current state of the form data.
   *
   * This function checks if there are no errors and if the specified field in the form data contains
   * valid information for a preview. It evaluates based on the existence of `data`, the field's type (e.g., "file" or "video"),
   * or the assumption that it's a file from the server if `data` is not present.
   *
   * @function
   * @returns {boolean} - Returns true if the preview should be displayed, false otherwise.
   */
  const shouldShowPreview = () => {
    if (!error && formData[fieldKey]) {
      if (formData[fieldKey].data) {
        // If we have data, there needs to be content also (because data may be null).
        return (
          formData[fieldKey].type === "file" ||
          formData[fieldKey].type === "video"
        );
      } else {
        // If we don't have "data", then we assume it's a file from the server and can display it.
        return true;
      }
    } else {
      return false;
    }
  };

  //We validate the data on onBlur, because with the previous approach, the error was displayed immediately,
  //and we therefore display at least one mandatory field error. However, this means that the error is no
  //longer visible when we delete the file. The error is only visible when you click in and out of the file
  //upload. We have to validate the file on change, because the file is not yet in the form data.
  //ToDo: validate file also on change
  const handleBlur = () => {
    const currentValue = formData[fieldKey];
    if (validateFile) {
      validateFile(fieldKey, currentValue);
    }
  };

  // Render either the preview of the file, or the field selection field.
  return (
    <div className={styles.formGroup}>
      {label && (
        <h2>
          {label}
          {required && "*"}
        </h2>
      )}
      {description && <p className={styles.description}>{description}</p>}
      <div
        className={styles.formContainerFile({ error: !!error })}
        onBlur={handleBlur}
      >
        {shouldShowPreview() ? (
          <PreviewFile
            fieldKey={fieldKey}
            file={formData[fieldKey]}
            formData={formData}
            setFormData={setFormData}
          />
        ) : (
          <SelectFile
            fieldKey={fieldKey}
            label={label ?? fieldKey}
            setFormData={setFormData}
            accept={accept}
            validateFile={validateFile}
            error={error}
          />
        )}
      </div>
      {error && (
        <p id={`${fieldKey}-error`} className={styles.error}>
          {error}
        </p>
      )}
    </div>
  );
};
